var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { alarmTypeLabel } from "@ui/utils/alarm-type-label";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { signalRService, NotificationType, } from "@utility/signalr-service";
import { normalizeMonitoringTimestamps } from "@utility/access-point-monitoring";
import { setTemperatureUpdate } from "@reducers/temperature/temperature-slice";
import { toast } from "@ui/utils/toaster";
import { addAccessPointToList, updateAccessPointInList, patchAccessPointUpdatedAt, } from "@reducers/access-points/slice/access-point-slice";
import { addNodeToList, updateNodeInList, } from "@reducers/nodes/slice/nodes-slice";
import { addAlarmNotification } from "@reducers/alarm-notifications/slice/alarm-notifications-slice";
let globalConnectionStarted = false;
let globalListenersRegistered = false;
function signalRDateToIso(value) {
    if (value == null)
        return null;
    if (typeof value === "string")
        return value;
    if (value instanceof Date)
        return value.toISOString();
    return null;
}
export const useSignalR = () => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    const location = useLocation();
    const locationRef = useRef(location.pathname);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const user = useSelector((state) => state.auth.user);
    // Get pagination state for dashboard and nodes
    const dashboardPageNumber = useSelector((state) => state.accessPoint.currentPageNumber);
    const nodesPageNumber = useSelector((state) => state.nodes.currentPageNumber);
    // Keep location ref updated
    useEffect(() => {
        locationRef.current = location.pathname;
    }, [location.pathname]);
    // Refs for pagination (to avoid stale closures)
    const dashboardPageRef = useRef(dashboardPageNumber);
    const nodesPageRef = useRef(nodesPageNumber);
    useEffect(() => {
        dashboardPageRef.current = dashboardPageNumber;
    }, [dashboardPageNumber]);
    useEffect(() => {
        nodesPageRef.current = nodesPageNumber;
    }, [nodesPageNumber]);
    // Handle temperature updates
    const handleTemperatureUpdate = useCallback((update) => {
        dispatch(setTemperatureUpdate(update));
        const iso = signalRDateToIso(update.accessPointUpdatedAt);
        if (iso && update.accessPointUid) {
            const mon = normalizeMonitoringTimestamps(update.endPointCommunicationMonitoring);
            dispatch(patchAccessPointUpdatedAt({
                accessPointUid: update.accessPointUid,
                updatedAt: iso,
                endPointCommunicationMonitoring: mon === null ? undefined : mon,
            }));
        }
    }, [dispatch]);
    const handleNotificationUpdate = useCallback((update) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const currentPath = locationRef.current;
        const isOnDashboardPage = currentPath === "/dashboard" || currentPath.startsWith("/dashboard/");
        const isOnNodesPage = currentPath === "/nodes" || currentPath.startsWith("/nodes/");
        switch (update.notificationType) {
            case NotificationType.AccessPointAdded:
                if (update.accessPoint) {
                    toast.success(t("toasts.accessPointAdded", {
                        name: update.accessPoint.accessPointName || update.accessPoint.accessPointUID,
                    }));
                    if (isOnDashboardPage && dashboardPageRef.current === 1) {
                        dispatch(addAccessPointToList(update.accessPoint));
                    }
                }
                break;
            case NotificationType.AccessPointConnected:
                toast.success(t("toasts.accessPointConnected", {
                    name: ((_a = update.accessPoint) === null || _a === void 0 ? void 0 : _a.accessPointName) || ((_b = update.accessPoint) === null || _b === void 0 ? void 0 : _b.accessPointUID),
                }));
                if (isOnDashboardPage && update.accessPoint) {
                    dispatch(updateAccessPointInList(update.accessPoint));
                }
                break;
            case NotificationType.AccessPointDisconnected:
                toast.warning(t("toasts.accessPointDisconnected", {
                    name: ((_c = update.accessPoint) === null || _c === void 0 ? void 0 : _c.accessPointName) || ((_d = update.accessPoint) === null || _d === void 0 ? void 0 : _d.accessPointUID),
                }));
                if (isOnDashboardPage && update.accessPoint) {
                    dispatch(updateAccessPointInList(update.accessPoint));
                }
                break;
            case NotificationType.NodeAdded:
                toast.success(t("toasts.nodeAdded", {
                    name: ((_e = update.node) === null || _e === void 0 ? void 0 : _e.nodeName) || ((_f = update.node) === null || _f === void 0 ? void 0 : _f.nodeUid),
                }));
                if (isOnNodesPage && update.node && nodesPageRef.current === 1) {
                    dispatch(addNodeToList(update.node));
                }
                break;
            case NotificationType.NodeDisconnected:
                toast.warning(t("toasts.nodeDisconnected", {
                    name: ((_g = update.node) === null || _g === void 0 ? void 0 : _g.nodeName) || ((_h = update.node) === null || _h === void 0 ? void 0 : _h.nodeUid),
                }));
                if (isOnNodesPage && update.node) {
                    dispatch(updateNodeInList(update.node));
                }
                break;
            case NotificationType.AccessPointTimestampUpdated: {
                const iso = signalRDateToIso(update.accessPointUpdatedAt);
                if (iso && update.accessPointUid) {
                    const mon = normalizeMonitoringTimestamps(update.endPointCommunicationMonitoring);
                    dispatch(patchAccessPointUpdatedAt({
                        accessPointUid: update.accessPointUid,
                        updatedAt: iso,
                        endPointCommunicationMonitoring: mon === null ? undefined : mon,
                    }));
                }
                break;
            }
            case NotificationType.AlarmGenerated:
                if (update.alarmNotification) {
                    const alarmTypeName = alarmTypeLabel(update.alarmNotification.alarmType, t, update.alarmNotification.sensorType);
                    const nodeName = update.alarmNotification.nodeName || t("toasts.unknownNode");
                    const apName = update.alarmNotification.accessPointName || t("toasts.unknownAccessPoint");
                    toast.error(t("toasts.alarmNotification", { alarmType: alarmTypeName, node: nodeName, ap: apName }));
                    dispatch(addAlarmNotification(update.alarmNotification));
                }
                break;
            default:
                console.warn("Unknown notification type:", update.notificationType);
        }
    }, [dispatch, t]);
    useEffect(() => {
        if (!isAuthenticated || !(user === null || user === void 0 ? void 0 : user.id)) {
            if (globalConnectionStarted) {
                signalRService.offTemperatureUpdate();
                signalRService.offNotificationUpdate();
                signalRService.stopConnection();
                globalConnectionStarted = false;
                globalListenersRegistered = false;
            }
            return;
        }
        if (globalConnectionStarted && globalListenersRegistered) {
            // Re-register handlers to ensure they're always active
            signalRService.offTemperatureUpdate();
            signalRService.offNotificationUpdate();
            signalRService.onTemperatureUpdate(handleTemperatureUpdate);
            signalRService.onNotificationUpdate(handleNotificationUpdate);
            return;
        }
        // Start connection
        const startConnection = () => __awaiter(void 0, void 0, void 0, function* () {
            if (globalConnectionStarted) {
                return;
            }
            try {
                yield signalRService.startConnection();
                globalConnectionStarted = true;
                signalRService.onTemperatureUpdate(handleTemperatureUpdate);
                signalRService.onNotificationUpdate(handleNotificationUpdate);
                globalListenersRegistered = true;
            }
            catch (error) {
                globalConnectionStarted = false;
                globalListenersRegistered = false;
            }
        });
        startConnection();
        return () => { };
    }, [
        isAuthenticated,
        user === null || user === void 0 ? void 0 : user.id,
        handleTemperatureUpdate,
        handleNotificationUpdate,
    ]);
    return {
        isConnected: signalRService.isConnected(),
        connectionState: signalRService.getConnectionState(),
    };
};
export const useSignalRStatus = () => {
    return {
        isConnected: signalRService.isConnected(),
        connectionState: signalRService.getConnectionState(),
    };
};
